/**
 * The signed-in app's URLs — the one list of them.
 *
 * Every page, and every place inside a page that a user would call "somewhere"
 * (a strategy open in the editor, a Prices tab, a wizard step), has a path
 * here, because the browser's Back, Forward, reload, bookmarks and "open in
 * new tab" all work on URLs and on nothing else. A screen that lives only in
 * React state is one the browser cannot return to: Back leaves the app instead.
 *
 * What is deliberately *not* in a URL is the active property. It is server-side
 * state per user, shared by every tab, so a URL naming one property while the
 * server prices another would be a URL that lies.
 *
 * The paths must never collide with what the server or the public pages own —
 * `/api`, `/logto`, `/static`, `/reset-password` and every legal document path
 * — which `routes.test.ts` holds this table to.
 */

import {
    ACCOUNT_SECTION_BY_KEY,
    accountSectionFromSlug,
    type AccountSectionKey,
} from "../components/account/account-sections";
import type { DraftsViewMode } from "../components/Drafts";
import { resolveLegalRoute } from "../components/legal/legalContent";
import { WIZARD_STEPS, type WizardStepKey } from "../components/onboarding/wizardSteps";
import {
    EDITOR_SECTION_BY_KEY,
    editorSectionFromSlug,
    type EditorSectionKey,
} from "../components/settings/editor-sections";
import { CALENDAR_VIEW_SLUGS, calendarViewFromSlug, type CalendarViewKey } from "./calendarViews";
import type { PageName } from "./pages";

type ParameterisedPage = "strategies" | "drafts" | "settings" | "onboarding" | "studyArchive" | "account";

/** Pages whose path carries nothing but the page itself. */
export type SimplePage = Exclude<PageName, ParameterisedPage>;

/**
 * A booking's stay, framed on the Prices page after "view in calendar" on
 * Recent Bookings. Everything the highlight needs is here: the impact it
 * shows is refetched from these four fields, and the room-kind name is read
 * off the rows the page already has.
 */
export interface BookingStay {
    /** Arrival — the first night. */
    from: string;
    /** Checkout — exclusive, exactly as `BookingHighlight.stayTo`. */
    to: string;
    roomKindId: string;
    canceled: boolean;
}

/**
 * The strategy pricing the property, in place of a strategy id: what a link
 * that has to outlive the next save names (an assistant's answer in a
 * transcript), since every save mints a new version under a new id. The
 * Strategies page opens the active strategy for it and replaces the address
 * with the real id. No id can collide with it — every id is a clock reading.
 */
export const ACTIVE_STRATEGY_ID = "active";

export type AppRoute =
    | { page: SimplePage }
    | {
        page: "strategies";
        strategyId?: string;
        /**
         * The section of the open strategy on screen, by its slug in the path
         * (`/strategies/:strategyId/last-minute`); absent for the strategy's
         * summary. Meaningless without `strategyId`.
         */
        section?: EditorSectionKey;
        /**
         * A strategy proposal Optivo sent, open for review — the link its
         * e-mail carries. It names a proposal, not the property: the proposal
         * belongs to one, and the page says so when another is open.
         */
        proposalId?: string;
    }
    | {
        page: "drafts";
        tab?: DraftsViewMode;
        stay?: BookingStay;
        /** A one-time request to open on one calendar view; consumed on arrival; read and written on the calendar tab only. */
        calendarView?: CalendarViewKey;
    }
    | { page: "settings"; newConnection?: boolean }
    | { page: "onboarding"; step?: WizardStepKey }
    | {
        page: "studyArchive";
        /**
         * One answer Optivo sent, open for reading — a study, recommended
         * settings or a letter — by its id; absent for the list.
         */
        entryId?: string;
    }
    | {
        page: "account";
        /**
         * The section of the account settings on screen, by its slug in the
         * path (`/account/ai-agents`); absent where the address names none,
         * and the page then opens the first its reader may open.
         */
        section?: AccountSectionKey;
    };

const SIMPLE_PAGE_PATHS: Record<SimplePage, string> = {
    dashboard: "/",
    performance: "/performance",
    recentBookings: "/recent-bookings",
    forecast: "/forecast",
    demandForecast: "/demand-forecast",
    segmentChannel: "/channels-segments",
    marketConditions: "/market-conditions",
    connections: "/connections",
    users: "/admin/users",
    wirePaymentsAdmin: "/admin/wire-payments",
    priceDraftsAdmin: "/admin/price-plans",
    connectionsAdmin: "/admin/connections",
    activityAdmin: "/admin/activity",
    strategyHelpAdmin: "/admin/strategy-help",
    supportRemindersAdmin: "/admin/support-reminders",
    featuresAdmin: "/admin/features",
};

const SIMPLE_PAGE_BY_PATH = new Map<string, SimplePage>(
    (Object.entries(SIMPLE_PAGE_PATHS) as [SimplePage, string][]).map(([page, path]) => [path, page]),
);

/**
 * The address the payments had while they were a page of their own. GoPay
 * returns a payment to the address it was handed — appending its own `?id=` —
 * and the renewal e-mails already sent link here, so it keeps opening the
 * payments, now a section of the account settings. Nothing builds it any
 * more: `routePath` names the section's own address.
 */
const LEGACY_BILLING_PATH = "/billing";

const PRICES_TAB_SEGMENTS: Record<DraftsViewMode, string | null> = {
    calendar: null,
    priceHistory: "history",
    uploadLog: "upload-log",
};

const PRICES_TAB_BY_SEGMENT = new Map<string, DraftsViewMode>(
    (Object.entries(PRICES_TAB_SEGMENTS) as [DraftsViewMode, string | null][])
        .filter((entry): entry is [DraftsViewMode, string] => entry[1] !== null)
        .map(([tab, segment]) => [segment, tab]),
);

/** `keyMetrics` → `key-metrics`: the step's stable key, written the way URLs are. */
function stepSlug(key: WizardStepKey): string {
    return key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

const STEP_BY_SLUG = new Map<string, WizardStepKey>(
    WIZARD_STEPS.map((step) => [stepSlug(step.key), step.key as WizardStepKey]),
);

function normalizePath(pathname: string): string {
    const trimmed = pathname.replace(/\/+$/, "");
    return trimmed === "" ? "/" : trimmed;
}

function decodeSegment(segment: string): string | null {
    try {
        const decoded = decodeURIComponent(segment);
        return decoded === "" ? null : decoded;
    } catch {
        return null;
    }
}

function isCalendarDate(value: string): boolean {
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function parseStay(params: URLSearchParams): BookingStay | null {
    const stay = params.get("stay");
    const roomKindId = params.get("roomKind");
    if (!stay || !roomKindId) return null;
    const match = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(stay);
    if (!match) return null;
    const [, from, to] = match;
    // A stay is at least one night, so checkout falls after arrival.
    if (!isCalendarDate(from) || !isCalendarDate(to) || to <= from) return null;
    return { from, to, roomKindId, canceled: params.get("canceled") === "1" };
}

function parsePricesQuery(
    search: string,
    tab: DraftsViewMode,
): Pick<Extract<AppRoute, { page: "drafts" }>, "stay" | "calendarView"> {
    const params = new URLSearchParams(search);
    const stay = parseStay(params);
    const calendarView = tab === "calendar" ? calendarViewFromSlug(params.get("view")?.toLowerCase()) : null;
    return {
        ...(stay ? { stay } : {}),
        ...(calendarView ? { calendarView } : {}),
    };
}

/**
 * Which screen a location names, or null when no page answers to it.
 *
 * Parameters a page does not know are dropped rather than refused — GoPay, for
 * one, appends its own `?id=` to the payments it returns to.
 */
export function parseRoute(pathname: string, search: string): AppRoute | null {
    const path = normalizePath(pathname);
    const simple = SIMPLE_PAGE_BY_PATH.get(path.toLowerCase());
    if (simple) return { page: simple };
    if (path.toLowerCase() === LEGACY_BILLING_PATH) return { page: "account", section: "billing" };

    const segments = path.split("/").slice(1);
    const head = segments[0]?.toLowerCase();
    const sub = segments[1];

    if (head === "strategies") {
        if (segments.length === 1) return { page: "strategies" };
        if (segments.length === 3) {
            if (sub.toLowerCase() === "proposals") {
                const proposalId = decodeSegment(segments[2]);
                return proposalId ? { page: "strategies", proposalId } : null;
            }
            const strategyId = decodeSegment(sub);
            if (!strategyId) return null;
            // A section that no longer exists opens the strategy rather than
            // failing: a bookmark outliving a renamed section still lands on
            // the strategy it names.
            const section = editorSectionFromSlug(segments[2].toLowerCase());
            return section ? { page: "strategies", strategyId, section } : { page: "strategies", strategyId };
        }
        if (segments.length !== 2) return null;
        const strategyId = decodeSegment(sub);
        return strategyId ? { page: "strategies", strategyId } : null;
    }

    if (head === "prices") {
        const tab = segments.length === 1
            ? "calendar"
            : segments.length === 2 ? PRICES_TAB_BY_SEGMENT.get(sub.toLowerCase()) : undefined;
        if (!tab) return null;
        return { page: "drafts", tab, ...parsePricesQuery(search, tab) };
    }

    if (head === "connections" && segments.length === 2) {
        const mode = sub.toLowerCase();
        if (mode === "edit") return { page: "settings", newConnection: false };
        if (mode === "new") return { page: "settings", newConnection: true };
        return null;
    }

    if (head === "study-archive") {
        if (segments.length === 1) return { page: "studyArchive" };
        if (segments.length !== 2) return null;
        const entryId = decodeSegment(sub);
        return entryId ? { page: "studyArchive", entryId } : null;
    }

    if (head === "account") {
        if (segments.length === 1) return { page: "account" };
        if (segments.length !== 2) return null;
        // A section that no longer exists opens the page rather than failing,
        // as a renamed editor section or wizard step does: the page then
        // opens on the first section its reader may open.
        const section = accountSectionFromSlug(sub.toLowerCase());
        return section ? { page: "account", section } : { page: "account" };
    }

    if (head === "onboarding") {
        if (segments.length === 1) return { page: "onboarding" };
        if (segments.length !== 2) return null;
        // A step that no longer exists resumes the wizard rather than failing:
        // a bookmark outliving a renamed step should still land somewhere real.
        const step = STEP_BY_SLUG.get(sub.toLowerCase());
        return step ? { page: "onboarding", step } : { page: "onboarding" };
    }

    return null;
}

function pricesSearch(route: Extract<AppRoute, { page: "drafts" }>): string {
    const params = new URLSearchParams();
    if (route.stay) {
        params.set("stay", `${route.stay.from}..${route.stay.to}`);
        params.set("roomKind", route.stay.roomKindId);
        if (route.stay.canceled) params.set("canceled", "1");
    }
    if (route.calendarView && (route.tab ?? "calendar") === "calendar") {
        params.set("view", CALENDAR_VIEW_SLUGS[route.calendarView]);
    }
    const query = params.toString();
    return query ? `?${query}` : "";
}

/** The URL of a screen. `parseRoute(routePath(route))` gives the route back. */
export function routePath(route: AppRoute): string {
    switch (route.page) {
        case "strategies":
            if (route.proposalId) return `/strategies/proposals/${encodeURIComponent(route.proposalId)}`;
            if (!route.strategyId) return "/strategies";
            return `/strategies/${encodeURIComponent(route.strategyId)}`
                + (route.section ? `/${EDITOR_SECTION_BY_KEY[route.section].slug}` : "");
        case "drafts": {
            const segment = PRICES_TAB_SEGMENTS[route.tab ?? "calendar"];
            return (segment ? `/prices/${segment}` : "/prices") + pricesSearch(route);
        }
        case "settings":
            return route.newConnection ? "/connections/new" : "/connections/edit";
        case "onboarding":
            return route.step ? `/onboarding/${stepSlug(route.step)}` : "/onboarding";
        case "studyArchive":
            return route.entryId ? `/study-archive/${encodeURIComponent(route.entryId)}` : "/study-archive";
        case "account":
            return route.section ? `/account/${ACCOUNT_SECTION_BY_KEY[route.section].slug}` : "/account";
        default:
            return SIMPLE_PAGE_PATHS[route.page];
    }
}

/**
 * A location named by its shape rather than its values — `/strategies/:strategyId`
 * rather than one name per strategy — for telemetry that groups by name.
 */
export function routePattern(pathname: string): string {
    const route = parseRoute(pathname, "");
    if (route) {
        if (route.page === "strategies" && route.proposalId) return "/strategies/proposals/:proposalId";
        if (route.page === "strategies" && route.strategyId) {
            return route.section ? "/strategies/:strategyId/:section" : "/strategies/:strategyId";
        }
        if (route.page === "onboarding" && route.step) return "/onboarding/:step";
        if (route.page === "studyArchive" && route.entryId) return "/study-archive/:entryId";
        if (route.page === "account" && route.section) return "/account/:section";
        return routePath(route);
    }
    const path = normalizePath(pathname).toLowerCase();
    if (path === "/" || path === "/reset-password" || resolveLegalRoute(path) !== null) return path;
    return "/*";
}

/**
 * The page to come back to after signing in, or null when there is nothing
 * worth coming back to — the dashboard is where sign-in lands anyway, and a
 * public page does not need a session to be read.
 */
export function appReturnPath(pathname: string, search: string): string | null {
    const route = parseRoute(pathname, search);
    if (!route || route.page === "dashboard") return null;
    return routePath(route);
}
